import { useEffect } from "react";
import { useTenant } from "./TenantProvider";
import { generateTenantColorRamp } from "./tenantColorRamp";

// Every one of these is either --color-primary itself, or a token the
// default palette derives from the blue-purple scale (globals.css) — the
// rail background, primary buttons, active tabs, and purple badges all
// trace back to one of these. Anything else in the design system (neutral
// tokens, other semantic colors) intentionally stays put.
const RAMP_PROPERTIES = [
  "--color-blue-purple-50",
  "--color-blue-purple-100",
  "--color-blue-purple-500",
  "--color-blue-purple-600",
  "--color-blue-purple-700",
  "--color-blue-purple-900",
  "--color-primary",
  "--color-badge-text-purple",
  "--color-badge-bg-light-purple",
  "--color-badge-border-purple",
] as const;

/**
 * Renders nothing. Mount once inside TenantProvider so tenant branding
 * (once loaded) overrides the design system's default CSS custom
 * properties — same mechanism the app already uses for `dir`/`lang`.
 */
export function TenantThemeApplier(): null {
  const { branding } = useTenant();

  useEffect(() => {
    const root = document.documentElement;
    const ramp =
      branding?.primaryColor !== undefined
        ? generateTenantColorRamp(branding.primaryColor)
        : null;

    if (ramp === null) {
      for (const property of RAMP_PROPERTIES) root.style.removeProperty(property);
      return;
    }

    root.style.setProperty("--color-blue-purple-50", ramp[50]);
    root.style.setProperty("--color-blue-purple-100", ramp[100]);
    root.style.setProperty("--color-blue-purple-500", ramp[500]);
    root.style.setProperty("--color-blue-purple-600", ramp[600]);
    root.style.setProperty("--color-blue-purple-700", ramp[700]);
    root.style.setProperty("--color-blue-purple-900", ramp[900]);
    root.style.setProperty("--color-primary", ramp[500]);
    root.style.setProperty("--color-badge-text-purple", ramp[700]);
    root.style.setProperty("--color-badge-bg-light-purple", ramp[50]);
    root.style.setProperty("--color-badge-border-purple", ramp[100]);
  }, [branding]);

  return null;
}
